import {
  ChildCard, EmptyState, ErrorState, LoadingState, PageHeader, Stack,
  useI18n,
} from '@daghfal/core';
import { Navigate } from 'react-router-dom';
import { childHome, presenceOf, useMyChildren } from '../myChildren';

/**
 * The guardian's chooser (§4.3).
 *
 * Built from `/me/children`, which is the relationship rather than a search: a parent sees their own
 * children and cannot reach anybody else's, and that follows from the guardian link rather than from a
 * permission an administrator could grant to the wrong person.
 *
 * A family with one child never sees this screen — it would be a list of one, and a tap nobody should have
 * to make (FR-SEL-03).
 */
export function ChildPicker() {
  const { t } = useI18n();
  const children = useMyChildren();

  if (children.isPending) return <LoadingState />;
  if (children.isError) {
    return <ErrorState error={children.error} onRetry={() => void children.refetch()} />;
  }

  // A child who has left is listed only while a balance is due, for the payments screen (Q32).
  const current = children.data.filter((child) => child.status === 'Active' || child.paymentsOnly);
  const list = current.length > 0 ? current : children.data;

  // No child on this membership, which is also what a teacher who chose their nursery here sees. The
  // chooser's hint ("your account belongs to more than one place") used to stand here, offering a choice
  // this screen does not have.
  if (list.length === 0) {
    return <EmptyState icon="🌷" title={t('state.empty')} body={t('children.none')} />;
  }

  if (list.length === 1) {
    const only = list[0]!;
    return <Navigate to={childHome(only)} replace />;
  }

  return (
    <Stack>
      <PageHeader title={t('app.daghfal')} lede={t('app.tagline')} />
      {list.map((child) => (
        <ChildCard
          key={child.childId}
          child={{
            id: child.childId,
            firstName: child.firstName,
            lastName: child.lastName,
            preferredName: child.preferredName,
            dateOfBirth: '',
            gender: 'Female',
            classId: null,
            className: child.className,
            status: child.status,
            allergyMarks: child.allergyMarks,
          }}
          photoUrl={child.photoUrl}
          presence={presenceOf(child)}
          meta={child.paymentsOnly ? t('children.paymentsOnly') : child.className}
          to={childHome(child)}
        />
      ))}
    </Stack>
  );
}
